import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Eye, Heart, Share2, Check, Minus, Plus, X } from 'lucide-react';

const BACKEND_URL = '';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showTryOn, setShowTryOn] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/products/${productId}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
        // Fetch related products
        fetchRelatedProducts(data.category);
      } else {
        navigate('/collection');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async (category) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products?category=${category}`);
      if (response.ok) {
        const data = await response.json();
        setRelatedProducts(data.filter(p => p.id !== productId).slice(0, 4));
      }
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  };

  const handleAddToCart = () => {
    // Get existing cart from localStorage
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    
    // Check if product already in cart
    const existingIndex = existingCart.findIndex(item => item.id === product.id);
    
    if (existingIndex >= 0) {
      existingCart[existingIndex].quantity += quantity;
    } else {
      existingCart.push({
        id: product.id,
        name: product.name,
        price: product.sale_price || product.price,
        image: product.images?.[0],
        quantity: quantity
      });
    }
    
    localStorage.setItem('cart', JSON.stringify(existingCart));
    setAddedToCart(true);
    
    // Redirect to cart after short delay
    setTimeout(() => {
      navigate('/cart');
    }, 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/collection" className="text-red-600 hover:underline">
            Back to Collection
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-red-600">Home</Link>
            <span className="text-gray-400">/</span>
            <Link to="/collection" className="text-gray-500 hover:text-red-600">Collection</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Main Product Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Product Image */}
            <div className="space-y-4">
              <div className="aspect-square bg-white rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={product.images?.[0] || 'https://via.placeholder.com/600x600?text=No+Image'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Virtual Try-On Button */}
              {product.has_virtual_tryon && (
                <button
                  onClick={() => setShowTryOn(true)}
                  className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white py-4 rounded-xl hover:bg-gray-800 transition-colors font-medium"
                >
                  <Eye className="w-5 h-5" />
                  Virtual Try-On
                </button>
              )}
            </div>

            {/* Product Details */}
            <div className="space-y-6">
              {/* Brand & Category */}
              <div className="flex items-center gap-4">
                {product.brand && (
                  <span className="text-red-600 font-medium">{product.brand}</span>
                )}
                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm capitalize">
                  {product.category}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{product.name}</h1>

              {/* Price */}
              <div className="flex items-center gap-4">
                {product.sale_price ? (
                  <>
                    <span className="text-4xl font-bold text-red-600">${product.sale_price}</span>
                    <span className="text-2xl text-gray-400 line-through">${product.price}</span>
                    <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-sm font-medium">
                      SALE
                    </span>
                  </>
                ) : (
                  <span className="text-4xl font-bold text-gray-900">${product.price}</span>
                )}
              </div>

              {/* Stock Status */}
              <div className={`flex items-center gap-2 ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                <span className={`w-3 h-3 rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></span>
                {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
              </div>

              {/* Description */}
              <div className="prose prose-gray">
                <p className="text-gray-600 text-lg leading-relaxed">{product.description}</p>
              </div>

              {/* SKU */}
              {product.sku && (
                <p className="text-sm text-gray-500">SKU: {product.sku}</p>
              )}

              {/* Quantity Selector */}
              <div className="flex items-center gap-4">
                <span className="font-medium text-gray-700">Quantity:</span>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-6 py-3 font-medium">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <div className="flex gap-4">
                <button
                  onClick={handleAddToCart}
                  disabled={product.stock === 0}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-lg transition-all ${
                    addedToCart 
                      ? 'bg-green-600 text-white' 
                      : product.stock === 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {addedToCart ? (
                    <>
                      <Check className="w-6 h-6" />
                      Added to Cart!
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-6 h-6" />
                      Add to Cart
                    </>
                  )}
                </button>
                <button className="p-4 border-2 border-gray-200 rounded-xl hover:border-red-600 hover:text-red-600 transition-colors">
                  <Heart className="w-6 h-6" />
                </button>
              </div>

              {/* Contact for Questions */}
              <div className="bg-gray-100 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2">Have Questions?</h3>
                <p className="text-gray-600 mb-4">Our opticians are here to help you find the perfect frames.</p>
                <Link
                  to="/contact"
                  className="inline-block text-red-600 font-medium hover:underline"
                >
                  Contact Us →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">You May Also Like</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {relatedProducts.map(relatedProduct => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-square">
                    <img 
                      src={relatedProduct.images?.[0] || 'https://via.placeholder.com/300x300'}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 group-hover:text-red-600 transition-colors">
                      {relatedProduct.name}
                    </h3>
                    <p className="text-red-600 font-bold">
                      ${relatedProduct.sale_price || relatedProduct.price}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Virtual Try-On Modal */}
      {showTryOn && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-bold">Virtual Try-On: {product.name}</h3>
              <button 
                onClick={() => setShowTryOn(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden mb-4">
                <VirtualTryOnComponent product={product} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-lg">{product.name}</h4>
                  <p className="text-red-600 font-medium">
                    ${product.sale_price || product.price}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowTryOn(false);
                    handleAddToCart();
                  }}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Virtual Try-On Component
const VirtualTryOnComponent = ({ product }) => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const videoRef = React.useRef(null);

  useEffect(() => {
    startCamera();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 1280, height: 720 } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      setError('Unable to access camera. Please allow camera permissions.');
      console.error('Camera error:', err);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-white">
        <div className="text-center">
          <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{error}</p>
          <button 
            onClick={startCamera}
            className="mt-4 bg-red-600 px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <video 
        ref={videoRef}
        autoPlay 
        playsInline 
        muted
        className="w-full h-full object-cover"
        style={{ transform: 'scaleX(-1)' }}
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-white text-center bg-black/50 px-6 py-3 rounded-lg">
          <p className="text-sm">Position your face in the center</p>
          <p className="text-xs opacity-75 mt-1">Virtual frame: {product.name}</p>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
